import { CinemaType } from '../types/entities';
import {toRad} from './numbers';

export interface PositionType {
  lat: number;
  lng: number;
}

/**
 * Calculates the distance between two positions using the Haversine formula.
 * 
 * @param pos_a The first position.
 * @param pos_b The second position.
 * @returns The distance in kilometers.
 */
export const haversine = (pos_a: PositionType, pos_b: PositionType) => {
  const lat2 = pos_b.lat,
      lon2 = pos_b.lng,
      lat1 = pos_a.lat,
      lon1 = pos_a.lng
  ;
  
  const R    = 6371; // km
  //has a problem with the .toRad() method below.
  const x1   = lat2 - lat1;
  const dLat = toRad(x1);
  const x2   = lon2 - lon1;
  const dLon = toRad(x2);
  const a    = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
  const c    = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const d    = R * c;

  return d;
};

const sortByDistance = (a: { d: number }, b: { d: number }) => {
  if (a.d === b.d) {
    return 0;
  } else if (a.d < b.d) {
    return -1;
  } else {
    return 1;
  }
};

/**
 * Filters the nearest cinemas based on the given latitude and longitude.
 * 
 * @param cinemas An array of cinema positions.
 * @param lat The latitude to compare against.
 * @param lng The longitude to compare against.
 * @param count The number of nearest cinemas to return.
 * @returns CinemaType[] An array of the nearest cinemas.
 */
export const filterCinemasNearLatLng = (cinemas: Array<CinemaType>, lat: number, lng: number, count = 3) => {
  const distances = cinemas.map(cinema => ({
    d: haversine({lat, lng}, cinema),
    cinema: cinema
  }));

  distances.sort(sortByDistance);

  return distances.slice(0, count).map(result => result.cinema);
};

export const calculateDistanceInKm = (pos_a: PositionType, pos_b: PositionType) => {
  console.log('Calculating distance between', pos_a, pos_b);
  return haversine(pos_a, pos_b);
};

export const getLocationIfGranted = (): Promise<PositionType | undefined> => {
  if (!navigator.geolocation) {
      return Promise.resolve(undefined);
  }

  return navigator.permissions.query({ name: 'geolocation' })
      .then(result => {
          if (result.state !== 'granted') {
              return undefined;
          }
          
          return new Promise<PositionType | undefined>((resolve) => {
              navigator.geolocation.getCurrentPosition(position => {
                  resolve({
                      lat: position.coords.latitude,
                      lng: position.coords.longitude,
                  });
              }, () => {
                  resolve(undefined);
              });
          });
      });
};